import { ColumnFilterElementTemplateOptions } from 'primereact/column';
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { FilterError, MatchMode } from '../../../../../types/types';
import GridInput from '../../../GridInput/GridInput';
import { isValuelessMatchMode } from '../../../../../utils/filters/isValuelessMatchMode';
import { Checkbox } from '../../../../Checkbox';
import SeptimLoadingSpinner from '../../../SeptimLoadingSpinner/SeptimLoadingSpinner';
import { useColumnFilterRegistryContext } from '../../../../../hooks/context/useColumnFilterRegistryContext';
import { useColumnFilterAutoFocus } from '../../../../../hooks/shortcuts/useColumnFilterAutoFocus';
import { useSuppressEscapeDefault } from '../../../../../hooks/shortcuts/useSuppressEscapeDefault';
import { useColumnFilterApplyShortcut } from '../../../../../hooks/shortcuts/useColumnFilterApplyShortcut';
import { useColumnFilterCloseShortcut } from '../../../../../hooks/shortcuts/useColumnFilterCloseShortcut';
import { useBooleanFilterSortGuard } from '../../../../../hooks/shortcuts/useBooleanFilterSortGuard';
import { CLOSE_OTHER_COLUMN_FILTERS_EVENT } from '../../../../../constants';
import { getIsBooleanColumnFilter } from './utils/getIsBooleanColumnFilter';
import { GridColumn } from '../../types';
import { useRequestDataContext } from '../../../../../hooks/context/useRequestDataContext';
import { shouldDropFilterValueOnMatchModeChange } from '../../../../../utils/filters/shouldDropFilterValueOnMatchModeChange';

export interface FilterTemplateProps {
  options: ColumnFilterElementTemplateOptions;
  column: GridColumn;
  filterErrors: Record<string, FilterError>;
  setFilterErrors: Dispatch<SetStateAction<Record<string, FilterError>>>;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  openFullscreenReference?: (
    column: GridColumn,
    value: string | null,
    matchMode: MatchMode,
    resolvedFilter?: Record<string, any> | null
  ) => void;
}

const FilterTemplate: FC<FilterTemplateProps> = ({
  options,
  column,
  filterErrors,
  setFilterErrors,
  referenceDatasets,
  setReferenceDatasets,
  openFullscreenReference,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const matchMode = options?.filterModel?.matchMode?.toString();
  const isBooleanColumnFilter = getIsBooleanColumnFilter(column);
  const requestData = useRequestDataContext();

  const { takeFilterSubmitted, closeColumnFilter } =
    useColumnFilterRegistryContext();

  // Keep latest options reachable from the cleanup closure (filterCallback identity may change between renders).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // PrimeReact owns the match mode dropdown, so the previous draft match mode is only knowable across renders; it must not trigger one.
  const previousMatchModeRef = useRef<string | undefined>(matchMode);

  useColumnFilterAutoFocus({
    wrapperRef,
    filterItem: column.filterItem,
    columnName: column.name,
  });
  useColumnFilterApplyShortcut(column.name);
  useColumnFilterCloseShortcut(column.name);
  useBooleanFilterSortGuard(column.name, isBooleanColumnFilter);
  // First Esc closes the filter (handled by useColumnFilterCloseShortcut above); this swallows the browser default too so it doesn't also exit element-fullscreen on the same keystroke.
  useSuppressEscapeDefault(true);

  // Snapshot the value and match mode PrimeReact shows on open (= currently applied filter). If the overlay closes without an Apply (Esc, click outside, etc.), restore that snapshot so a stale draft does not survive into the next open.
  useEffect(() => {
    const initialValue = options.value;
    const initialMatchMode = options.filterModel.matchMode;
    const columnName = column.name;
    return () => {
      if (takeFilterSubmitted(columnName)) return;
      optionsRef.current.filterModel.matchMode = initialMatchMode;
      optionsRef.current.filterCallback(initialValue);
    };
  }, []);

  // Layout effect: runs before paint (no flash of "true") and before the input's own mount default.
  useLayoutEffect(() => {
    const previousMatchMode = previousMatchModeRef.current;
    previousMatchModeRef.current = matchMode;

    if (isBooleanColumnFilter) return;
    if (
      !shouldDropFilterValueOnMatchModeChange(
        previousMatchMode as MatchMode,
        matchMode as MatchMode,
        options.value
      )
    ) {
      return;
    }

    options.filterCallback(null);
  }, [matchMode]);

  // Cross-grid coordination: when another column filter opens (master ↔ slave or same grid), the opener dispatches this event with the target column name as `except`. If our column does not match, close ourselves so only one overlay stays open.
  useEffect(() => {
    const onCloseOthers = (event: Event) => {
      const except = (event as CustomEvent<{ except?: string }>).detail?.except;
      if (except === column.name) return;
      closeColumnFilter(column.name);
    };
    window.addEventListener(CLOSE_OTHER_COLUMN_FILTERS_EVENT, onCloseOthers);
    return () =>
      window.removeEventListener(
        CLOSE_OTHER_COLUMN_FILTERS_EVENT,
        onCloseOthers
      );
  }, [column.name, closeColumnFilter]);

  const setError = (error: FilterError) => {
    setFilterErrors((v) => ({ ...v, [column.name]: error }));
  };

  if (isValuelessMatchMode(matchMode as MatchMode)) {
    return null;
  }

  if (!column.filterItem) {
    return (
      <div className="column-filter-loading-container">
        <SeptimLoadingSpinner />
      </div>
    );
  }

  if (isBooleanColumnFilter) {
    return (
      <div ref={wrapperRef} className="boolean-filter-container">
        <Checkbox
          value={options.value}
          onChange={(value) => options.filterCallback(value)}
          color="secondary"
        />
        <label htmlFor={`${options.field}_filterInput`}>{column.label}</label>
      </div>
    );
  }

  return (
    <div ref={wrapperRef}>
      <GridInput
        name={`${options.field}_filterInput`}
        type={column.filterItem.type}
        value={options.value}
        onChange={(value) => options.filterCallback(value)}
        matchMode={matchMode}
        error={filterErrors[column.name]}
        setError={setError}
        control={column.filterItem.control}
        isColumnFilter
        referenceDatasets={referenceDatasets}
        setReferenceDatasets={setReferenceDatasets}
        requestData={requestData}
        openFullscreenReference={
          openFullscreenReference
            ? (resolvedFilter) =>
                openFullscreenReference(
                  column,
                  options.value,
                  matchMode as MatchMode,
                  resolvedFilter
                )
            : undefined
        }
      />
    </div>
  );
};

export default FilterTemplate;
